import { useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { avatarColor, avatarInitials } from '~/lib/avatar'

/**
 * The signed-in account as one circle: the picture on the Google account, or the initials on
 * a colour derived from the account when there is none. Both the header and settings draw it
 * through here, so one person is one circle wherever they appear.
 */
export function AccountAvatar({ size, className = '' }: { size: number; className?: string }) {
  const { session } = useAuth()
  const picture = session?.user.picture ?? null

  /*
   * A Google picture URL can 404 or be blocked, and a broken image would leave an empty
   * circle where a face was. Falling back on the error puts the initials there instead.
   */
  const [broken, setBroken] = useState(false)
  const style = { width: size, height: size }

  if (picture !== null && !broken) {
    return (
      <img
        src={picture}
        // Named after the person, not after what it leads to: whatever carries the circle
        // says that part already, and a picture of someone is worth naming as one.
        alt={session?.user.name ?? 'Account'}
        referrerPolicy="no-referrer"
        onError={() => {
          setBroken(true)
        }}
        className={`shrink-0 rounded-full bg-surface-raised object-cover ${className}`}
        style={style}
      />
    )
  }

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full font-medium text-white ${className}`}
      // Sized off the circle, so the initials fill it at every size it is drawn at.
      style={{
        ...style,
        backgroundColor: avatarColor(session?.user.email ?? ''),
        fontSize: size / 2.5,
      }}
    >
      {avatarInitials(session?.user.name)}
    </span>
  )
}
